import { 
  IPurchaseUseCase 
} from '../usecases/purchase.usecase';
import {
  ICartUseCase
} from '../usecases/cart.usecase';
import { 
  IUserRepository 
} from '../repository/user.repository';
import { 
  IAnalyticsUseCase 
} from '../usecases/analytics.usecase';

/**
 * Order Service
 * Handles order fulfillment after successful payments
 * Manages the flow from successful Stripe payment to delivered items
 */
export class OrderService {
  constructor(
    private purchaseUseCase: IPurchaseUseCase,
    private cartUseCase: ICartUseCase,
    private userRepository: IUserRepository,
    private analyticsUseCase: IAnalyticsUseCase
  ) {
    console.log('[OrderService] Constructor - Initialized');
  }

  // ===== ORDER FULFILLMENT FROM CART =====

  /**
   * Handle successful cart checkout
   * Called from success page or webhook after Stripe payment
   */
  async fulfillCartOrder(orderData: {
    sessionId: string;
    userId: string;
    purchaseId?: string;
  }): Promise<{
    success: boolean;
    orderId?: string;
    items?: any[];
    error?: string;
  }> {
    console.log('[OrderService.fulfillCartOrder] START - OrderData:', orderData);
    
    try {
      // Step 1: Get purchase record by session ID or purchase ID
      let purchase: any;
      if (orderData.purchaseId) {
        purchase = await this.purchaseUseCase.getPurchaseById(orderData.purchaseId);
      } else {
        // Find purchase by Stripe session ID
        purchase = await this.findPurchaseBySessionId(orderData.sessionId);
      }

      if (!purchase) {
        throw new Error('Purchase not found for this session');
      }

      // Step 2: Verify purchase belongs to user
      if (purchase.userId !== orderData.userId) {
        throw new Error('Purchase does not belong to this user');
      }

      // Step 3: Check if order is already fulfilled
      if (purchase.fulfillmentStatus === 'fulfilled') {
        console.log('[OrderService.fulfillCartOrder] Order already fulfilled:', purchase.id);
        return {
          success: true,
          orderId: purchase.id,
          items: purchase.items
        };
      }

      // Step 4: Fulfill the order - grant access to purchased resources
      const fulfilledItems = [];
      for (const item of purchase.items) {
        if (item.itemType === 'resource') {
          // Grant access to curriculum resource
          await this.grantResourceAccess(orderData.userId, item.itemId);
          fulfilledItems.push({
            ...item,
            grantedAt: new Date().toISOString()
          });
        }
      }

      // Step 5: Update purchase record as fulfilled
      await this.purchaseUseCase.updatePurchase(purchase.id, {
        fulfillmentStatus: 'fulfilled',
        fulfilledAt: new Date(),
        fulfilledItems: fulfilledItems
      });

      // Step 6: Clear user's cart (items are now owned)
      const userCart = await this.cartUseCase.getUserActiveCart(orderData.userId);
      if (userCart) {
        await this.cartUseCase.clearCart(userCart.id);
      }

      // Step 7: Track successful order fulfillment
      await this.analyticsUseCase.trackActivity(
        orderData.userId,
        'orderFulfilled',
        {
          orderId: purchase.id,
          session_id: orderData.sessionId,
          itemsCount: fulfilledItems.length,
          totalAmount: purchase.totalAmount,
          items: fulfilledItems.map(item => ({
            id: item.itemId,
            type: item.itemType,
            price: item.price
          }))
        }
      );

      console.log('[OrderService.fulfillCartOrder] SUCCESS - Order fulfilled:', purchase.id);
      return {
        success: true,
        orderId: purchase.id,
        items: fulfilledItems
      };

    } catch (error) {
      console.error('[OrderService.fulfillCartOrder] ERROR:', error);
      
      // Track failed fulfillment
      try {
        await this.analyticsUseCase.trackActivity(
          orderData.userId,
          'orderFulfillmentFailed',
          {
            session_id: orderData.sessionId,
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        );
      } catch (trackingError) {
        console.error('[OrderService.fulfillCartOrder] Analytics tracking error:', trackingError);
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // ===== ORDER MANAGEMENT =====

  /**
   * Get user's order history
   */
  async getUserOrders(userId: string): Promise<{
    orders: any[];
    totalOrders: number;
    totalSpent: number;
  }> {
    console.log('[OrderService.getUserOrders] START - UserId:', userId);
    
    try {
      // Get all fulfilled purchases for user
      const purchases = await this.purchaseUseCase.getUserPurchases(userId);
      const fulfilledOrders = purchases.filter(p => p.fulfillmentStatus === 'fulfilled');
      
      const totalSpent = fulfilledOrders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);

      console.log('[OrderService.getUserOrders] SUCCESS - Found', fulfilledOrders.length, 'orders');
      return {
        orders: fulfilledOrders,
        totalOrders: fulfilledOrders.length,
        totalSpent
      };

    } catch (error) {
      console.error('[OrderService.getUserOrders] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get specific order details
   */
  async getOrderById(orderId: string, userId: string): Promise<{
    order: any | null;
    items: any[];
  }> {
    console.log('[OrderService.getOrderById] START - OrderId:', orderId, 'UserId:', userId);
    
    try {
      const purchase = await this.purchaseUseCase.getPurchaseById(orderId);
      
      if (!purchase) {
        return { order: null, items: [] };
      }

      // Verify order belongs to user
      if (purchase.userId !== userId) {
        throw new Error('Order does not belong to this user');
      }

      console.log('[OrderService.getOrderById] SUCCESS - Order found:', orderId);
      return {
        order: purchase,
        items: purchase.fulfilledItems || purchase.items || []
      };

    } catch (error) {
      console.error('[OrderService.getOrderById] ERROR:', error);
      throw error;
    }
  }

  // ===== HELPER METHODS =====

  /**
   * Find purchase record by Stripe session ID
   */
  private async findPurchaseBySessionId(sessionId: string): Promise<any> {
    console.log('[OrderService.findPurchaseBySessionId] START - SessionId:', sessionId);
    
    try {
      // This would typically query purchases by stripeSessionId
      // For now, we'll use the purchase use case to find it
      const purchases = await this.purchaseUseCase.getPurchases({ 
        stripeSessionId: sessionId 
      });
      
      return purchases.length > 0 ? purchases[0] : null;
    } catch (error) {
      console.error('[OrderService.findPurchaseBySessionId] ERROR:', error);
      return null;
    }
  }

  /**
   * Grant access to a curriculum resource
   */
  private async grantResourceAccess(userId: string, resourceId: string): Promise<void> {
    console.log('[OrderService.grantResourceAccess] START - UserId:', userId, 'ResourceId:', resourceId);
    
    try {
      // This would typically create a userResourceAccess record
      // For now, we'll track it in analytics
      await this.analyticsUseCase.trackActivity(
        userId,
        'resourceAccessGranted',
        {
          resourceId: resourceId,
          grantedAt: new Date().toISOString()
        }
      );

      console.log('[OrderService.grantResourceAccess] SUCCESS - Access granted');
    } catch (error) {
      console.error('[OrderService.grantResourceAccess] ERROR:', error);
      throw error;
    }
  }

  /**
   * Check if user owns a specific resource
   */
  async userOwnsResource(userId: string, resourceId: string): Promise<boolean> {
    console.log('[OrderService.userOwnsResource] START - UserId:', userId, 'ResourceId:', resourceId);
    
    try {
      // Get user's fulfilled orders
      const userOrders = await this.getUserOrders(userId);
      
      // Check if any order contains this resource
      const ownsResource = userOrders.orders.some(order => 
        order.fulfilledItems?.some((item: any) => 
          item.itemId === resourceId && item.itemType === 'resource'
        )
      );

      console.log('[OrderService.userOwnsResource] SUCCESS - Owns resource:', ownsResource);
      return ownsResource;

    } catch (error) {
      console.error('[OrderService.userOwnsResource] ERROR:', error);
      return false;
    }
  }

  /**
   * Get user's owned resources
   */
  async getUserOwnedResources(userId: string): Promise<{
    resources: any[];
    totalResources: number;
  }> {
    console.log('[OrderService.getUserOwnedResources] START - UserId:', userId);
    
    try {
      const userOrders = await this.getUserOrders(userId);
      
      // Extract all resources from fulfilled orders
      const ownedResources: any[] = [];
      userOrders.orders.forEach(order => {
        if (order.fulfilledItems) {
          order.fulfilledItems.forEach((item: any) => {
            if (item.itemType === 'resource') {
              ownedResources.push({
                ...item,
                purchasedAt: order.fulfilledAt,
                orderId: order.id
              });
            }
          });
        }
      });

      console.log('[OrderService.getUserOwnedResources] SUCCESS - Found', ownedResources.length, 'owned resources');
      return {
        resources: ownedResources,
        totalResources: ownedResources.length
      };

    } catch (error) {
      console.error('[OrderService.getUserOwnedResources] ERROR:', error);
      throw error;
    }
  }
}
